import {
  setSelectedDuration,
  setSelectedPackage,
  setSelectedPackageType,
} from 'redux/payments/payments.actions';
import styles from './PackageUpgrade.module.scss';
import useTranslation from 'hooks/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { capitalizeFirstLetter } from 'utils/text';
import { SelectPackage } from '.';
import { getDurationKey, getIcon } from 'utils/packages';
import { CloseOutlinedIcon, EqualCircleIconComponent } from 'assets/icons';
import { useModal } from 'hooks';
import Modal from 'components/Modal';
import ModalHeader from 'components/Modal/ModalHeader';
import ModalBody from 'components/Modal/ModalBody';
import PackagesCompareContent from './PackagesCompareContent';

const getAccountType = ({ userData }) => userData.profile.accountType;
const getPackagesTypes = ({ paymentsData }) => paymentsData.packageTypes;
const getSelectedDuration = ({ paymentsData }) => paymentsData.selectedDuration;
const getPackages = ({ paymentsData }) => paymentsData.packages;
const getSelectedPackage = ({ paymentsData }) => paymentsData.selectedPackage;

const getDiscountPercentage = (item, selectedDuration) => {
  if (selectedDuration === 'monthly') {
    return 0;
  }

  if (selectedDuration === 'quarterly') {
    return 0;
  }

  if (selectedDuration === 'yearly') {
    if (item === 'commerical') return 10;

    if (item === 'growth') return 10;

    if (item === 'premium') return 10;

    if (item === 'premiumPlus') return 10;

    if (item === 'professional') return 10;
  }

  if (selectedDuration === 'triennial') {
    if (item === 'professional') return 25;

    if (item === 'commerical') return 25;

    if (item === 'premium') return 25;

    if (item === 'premiumPlus') return 25;
  }

  return 0;
};

function PackageUpgradeStep1({ setStep }) {
  const dispatch = useDispatch();
  const packages = useSelector(getPackages);
  const packageTypes = useSelector(getPackagesTypes);
  const accountType = useSelector(getAccountType);
  const selectedDuration = useSelector(getSelectedDuration);
  const selectedPackage = useSelector(getSelectedPackage);
  const [visible, toggle] = useModal();
  const { getTranslation, getAppLocale } = useTranslation('PACKAGE_UPGRADING');
  const locale = getAppLocale();
  const { t } = getTranslation();

  const getDiscountText = (number) => {
    return `${number}% ${t('DISCOUNT_CODE_PERCENT')}`;
  };

  const getDiscountDescription = (duration) => {
    if (selectedPackage === 'professional') {
      return '';
    }

    if (duration === 'yearly') {
      return getDiscountText(10);
    }

    if (duration === 'triennial') {
      return getDiscountText(25);
    }

    return '';
  };

  const DURATIONS = [
    {
      value_to_render: t('MONTHLY'),
      value_to_be_stored: 'monthly',
      discount: getDiscountDescription('monthly'),
    },
    {
      value_to_render: t('QUARTERLY'),
      value_to_be_stored: 'quarterly',
      discount: getDiscountDescription('quarterly'),
    },
    {
      value_to_render: t('YEARLY'),
      value_to_be_stored: 'yearly',
      discount: getDiscountDescription('yearly'),
    },
    {
      value_to_render: t('TRIENNIAL'),
      value_to_be_stored: 'triennial',
      discount: getDiscountDescription('triennial'),
    },
  ];

  const packagesPurchaseValue = {
    trial: 0,
    expire: 0,
    professional: 1,
    commerical: 2,
    growth: 2,
    premium: 3,
    premiumPlus: 4,
    vip: 5,
  };

  // returns all packages that are higher in their purchase values and their duration is eligible
  const renderPackagesAccordingToRankAndDuration = () => {
    const currentPurchasedPackage = packageTypes?.[accountType]?.['en'];
    const allPackages = Object.keys(packages.data);
    const currentPackageValue = packagesPurchaseValue[currentPurchasedPackage];

    return allPackages.filter((pack) => {
      return (
        packagesPurchaseValue[pack] >= currentPackageValue &&
        !isDurationNotElligible(pack)
      );
    });
  };

  const handlePackageChange = (name, type) => {
    dispatch(setSelectedPackage(name));
    dispatch(setSelectedPackageType(type));
    setStep('STEP_2');
  };

  // 'professional'
  // 'commerical'
  // 'premium'
  // 'premiumPlus'

  const isDurationNotElligible = (item) => {
    if (selectedDuration === 'monthly') {
      return item === 'professional';
    }

    if (selectedDuration === 'quarterly') {
      return item === 'professional';
    }

    if (selectedDuration === 'yearly') {
      return false;
    }

    if (selectedDuration === 'triennial') {
      return item === 'professional';
    }

    return false;
  };

  return (
    <div className=''>
      <div className={`${styles.header}`}>
        <h2>{t('CHOOSE_PACKAGE')}</h2>

        <div className={styles.compare} onClick={() => toggle()}>
          <EqualCircleIconComponent />

          <span>{t('COMPARE')}</span>
        </div>
      </div>

      <div className={styles.duration_select_wrapper}>
        <div className={styles.duration_selection}>
          {DURATIONS.map((d, idx) => {
            return (
              <div
                key={idx}
                onClick={() => {
                  dispatch(setSelectedDuration(d.value_to_be_stored));
                }}
                data-selected={selectedDuration === d.value_to_be_stored}
                className={styles.duration_item}
              >
                {d.value_to_render}
                {d.discount && (
                  <div className={styles.discountianno}>{d.discount}</div>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.active_package}>
          <span className={styles.active_package_title}>
            {t('ACTIVE_PACKAGE')}
          </span>

          <div className={styles.active_package_icon_text}>
            <img
              className={styles.active_package_icon}
              src={getIcon(packageTypes?.[accountType]?.['en'])}
              alt=''
            />

            <span className={styles.active_package_text}>
              {capitalizeFirstLetter(packageTypes?.[accountType]?.[locale])}
            </span>
          </div>
        </div>
      </div>

      <div className={`${styles.packagesWrapper}`}>
        {/* only display packages that purchasing them is not a downgrade */}
        {renderPackagesAccordingToRankAndDuration()
          .reverse()
          .map(
            (item) =>
              !isNaN(
                packages.data[item][getDurationKey(selectedDuration)][locale]
              ) && (
                <SelectPackage
                  name={item}
                  title={packages.data[item].name[getAppLocale()]}
                  description={packages.data[item].description[getAppLocale()]}
                  onSelect={() =>
                    handlePackageChange(item, packages.data[item].packageType)
                  }
                  features={packages.data[item].features[locale]}
                  icon={getIcon(item)}
                  price={
                    packages.data[item][getDurationKey(selectedDuration)][
                      locale
                    ]
                  }
                  unit={t('UNIT')}
                  discountP={getDiscountPercentage(item, selectedDuration)}
                  disabled={isDurationNotElligible(item)}
                  popular={item === 'premiumPlus'}
                  specialDiscountForNewStartups={item === 'premiumPlus'}
                />
              )
          )}
      </div>

      <Modal visible={visible} className={styles.compareModal}>
        <ModalHeader title={t('COMPARE')}>
          <img
            className={styles.closeIcon}
            src={CloseOutlinedIcon}
            alt=''
            onClick={() => toggle()}
          />
        </ModalHeader>
        <ModalBody>
          <PackagesCompareContent />
        </ModalBody>
      </Modal>
    </div>
  );
}

export default PackageUpgradeStep1;
